
import openPreviewPage from "../previewSchedulePage/actions/openPreviewPage";
import { previewSchedulePageStore } from "../previewSchedulePage";
import * as moment from "moment";

export enum GenerateOptions {
    REPLACE = "replace",
    CONSIDER = "consider"
}

/**
 * Shared Smart Schedule helper utilities.
 * handleReviewGeneratedSchedule now accepts generateOption derived from job status (overrideExistingSchedule).
 * generateOption values: 'replace' | 'consider'.
 */
export function handleReviewGeneratedSchedule(
    tagId: string,
    startDate: string,
    endDate: string,
    jobId: string,
    generateOption?: GenerateOptions,
    onNavigate?: () => void
): void {
    openPreviewPage(previewSchedulePageStore(), true, tagId, startDate, endDate, jobId, generateOption);
}

/** Map boolean overrideExistingSchedule -> generateOption string */
export function mapOverrideToGenerateOption(overrideExistingSchedule?: boolean): GenerateOptions | undefined {
    if (overrideExistingSchedule === undefined) {
        return undefined;
    }
    return overrideExistingSchedule ? GenerateOptions.REPLACE : GenerateOptions.CONSIDER;
}

/**
 * Compute UTC datetime boundaries for schedule generation.
 * Combines user's selected start date with earliest shift block start time (UTC),
 * and user's selected end date with latest shift block end time (UTC).
 * If shiftBlocks is empty, falls back to start of startDate and end of endDate (UTC).
 */
export function computeSmartScheduleBoundaries(
    shiftBlocks: Array<{ startTime: any; endTime: any }> | undefined,
    userStartDate: any,
    userEndDate: any,
    teamTimezone: string
): { startDateBoundaryUtc: string; endDateBoundaryUtc: string } {
    if (shiftBlocks && shiftBlocks.length > 0) {
        // Shift blocks in the UI have startTime/endTime as full ISO timestamps (though DB stores only HH:mm:ss).
        // We only care about the time-of-day portion, so parse as UTC and convert to team timezone
        // to extract the hour:minute:second that represents the shift template time in the team's timezone.
        const startMoments = shiftBlocks.map(sb => moment.utc(sb.startTime).tz(teamTimezone));
        const endMomentsOriginal = shiftBlocks.map(sb => moment.utc(sb.endTime).tz(teamTimezone));

        // Helper to pick earliest/latest by time-of-day (hour:minute) not by absolute timestamp
        // This ensures we compare 9am vs 5pm, not Jan 27 vs Jan 28
        const pickByTimeOfDay = (arr: moment.Moment[], comparator: (a: number, b: number) => boolean) => {
            return arr.reduce((acc, cur) => {
                const accMinutes = acc.hour() * 60 + acc.minute();
                const curMinutes = cur.hour() * 60 + cur.minute();
                return comparator(curMinutes, accMinutes) ? cur : acc;
            });
        };
        const earliestStartLocal = pickByTimeOfDay(startMoments, (a, b) => a < b);

        // Rebase end times onto userEndDate's date part. If an end spans into next day relative to its start (overnight), add +1 day.
        const userEndDateBase = moment(userEndDate).startOf('day');
        const rebasedEndMoments = endMomentsOriginal.map((endMoment, idx) => {
            const startMoment = startMoments[idx];
            const rebased = userEndDateBase.clone().set({
                hour: endMoment.hour(),
                minute: endMoment.minute(),
                second: endMoment.second(),
                millisecond: 0
            });
            // If original end crosses midnight (original end calendar date > original start OR end time-of-day < start time-of-day), treat as next day
            if (endMoment.isAfter(startMoment, 'day') || endMoment.hour() < startMoment.hour() || (endMoment.hour() === startMoment.hour() && endMoment.minute() < startMoment.minute())) {
                rebased.add(1, 'day');
            }
            return rebased;
        });
        const latestEndLocal = pickByTimeOfDay(rebasedEndMoments, (a, b) => a > b);

        // Combine start using userStartDate date part and earliest start time
        const userStartDateLocal = moment(userStartDate).startOf('day');
        const combinedStart = userStartDateLocal.clone().set({
            hour: earliestStartLocal.hour(),
            minute: earliestStartLocal.minute(),
            second: earliestStartLocal.second(),
            millisecond: 0
        }).utc();

        const combinedEnd = latestEndLocal.clone().utc();

        return {
            startDateBoundaryUtc: combinedStart.toISOString(),
            endDateBoundaryUtc: combinedEnd.toISOString()
        };
    }
    // Fallback: no shift blocks; use start/end of day after combining then convert
    const startFallback = moment(userStartDate).startOf('day').utc().toISOString();
    const endFallback = moment(userEndDate).endOf('day').utc().toISOString();
    return { startDateBoundaryUtc: startFallback, endDateBoundaryUtc: endFallback };
}

/**
 * Check if a job failure is outdated (older than 1 day).
 * Used to suppress stale failure UI after 1 day.
 * @param jobCompletedTime ISO string of the job completion time
 * @returns true if the failure is older than 1 day, false otherwise
 */
export function isOutdatedFailure(jobCompletedTime?: string): boolean {
    if (!jobCompletedTime) {
        return false;
    }
    const completedMoment = moment(jobCompletedTime);
    return completedMoment.isValid() && moment().diff(completedMoment, 'days') >= 1;
}

export default {
    handleReviewGeneratedSchedule,
    mapOverrideToGenerateOption,
    computeSmartScheduleBoundaries,
    isOutdatedFailure
};